import ForeCasterTableCard from "./ForeCasterTable/ForeCasterTableCard";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import {
  Grid,
  Autocomplete,
  Button,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedForecastStatus,
  setSelectedYear,
  setSelectedSharedUid,
  setSiteOptions,
  setSelectedSite,
} from "../../../../features/forecastersSlice";
import store from "../../../../store";
import { forecastersEndPointLogic } from "../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic";
import { useLocation, useSearchParams } from "react-router-dom";
import SharedStateContext from "../../../SharedStateProvider";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../Page404/Page403";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import ForeCastDiscussion from "./ForeCasterDiscussion/ForeCastDiscussion";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";

const PerformanceForCaster = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [, setTableRefresh] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSharedUid = useSelector(
    (state: any) => state.forecasters.selectedSharedUid
  );
  const siteOptions = useSelector(
    (state: any) => state.forecasters.siteOptions
  );
  const selectedYear = useSelector(
    (state: any) => state?.forecasters?.selectedYear
  );
  const selectedForecastStatus = useSelector(
    (state: any) => state?.forecasters?.selectedForecastStatus
  );
  const location = useLocation();
  const dispatch = useDispatch();
  const metaSharedUidFromURL = searchParams.get("metaSharedUid");
  const contentSharedUidFromURL = searchParams.get("contentSharedUid");
  const predictionFocusFromURL = searchParams.get("predictionFocus");
  const monthFromURL = searchParams.get("month");
  const selectedMonthFromURL = searchParams.get("selectedMonth");

  useEffect(() => {
    if (
      metaSharedUidFromURL ||
      contentSharedUidFromURL ||
      predictionFocusFromURL ||
      monthFromURL ||
      selectedMonthFromURL
    ) {
      const newSearchParams = new URLSearchParams(window.location.search); // Create a copy of the current search parameters
      newSearchParams.delete("metaSharedUid"); // Remove the metaSharedUid from the URL
      newSearchParams.delete("contentSharedUid"); // Remove the contentSharedUid from the URL
      newSearchParams.delete("predictionFocus"); // Remove the predictionFocus from the URL
      newSearchParams.delete("month"); // Remove the month from the URL
      newSearchParams.delete("selectedMonth"); // Remove the selectedMonth from the URL
      window.history.replaceState(
        {},
        "",
        `${location.pathname}?${newSearchParams}`
      ); // Replace the URL without reloading
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaSharedUidFromURL, contentSharedUidFromURL, predictionFocusFromURL]);

  // Reset state only when component unmounts, not on mount
  useEffect(() => {
    return () => {
      // Reset when component unmounts (navigating away)
      const currentYear = new Date().getFullYear();
      store.dispatch(setSelectedSharedUid(null));
      store.dispatch(setSelectedSite(null));
      store.dispatch(setSelectedYear(currentYear.toString()));
      store.dispatch(setSelectedForecastStatus(""));
    };
  }, []);

  const yearList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Generate years from currentYear to (currentYear + 10)
    for (let year = currentYear; year <= currentYear + 10; year++) {
      years.push({ label: year.toString() });
    }
    return years;
  }, []);

  const StatusList = [{ label: "publish" }, { label: "draft" }];

  // --> Get website list those have permisssions
  useEffect(() => {
    if (response?.data?.website_permissions) {
      const siteOptions = response.data.website_permissions.map(
        (siteObj: any) => {
          const uid = Object.keys(siteObj)[0]; // UID of the site
          const siteName = siteObj[uid].ui_site_name; // Site name
          return { uid, siteName }; // Return as an object with uid and siteName
        }
      );
      store.dispatch(setSiteOptions(siteOptions)); // Dispatch the memoized options
    }
  }, [response]);

  useEffect(() => {
    if (!selectedSharedUid) {
      setSearch(false);
      setDataShow(false);
    }
    setLoad(false);
    // eslint-disable-next-line
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite");
    const yearFromURL = searchParams.get("selectedYear");
    const statusFromURL = searchParams.get("selectedStatus");
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("selectedYear");
    updatedParams.delete("selectedStatus");
    updatedParams.delete("metaSharedUid");
    updatedParams.delete("contentSharedUid");
    updatedParams.delete("predictionFocus");
    updatedParams.delete("month");
    updatedParams.delete("selectedMonth");
    setSearchParams(updatedParams, { replace: true });

    // Check if we have a site in the URL and siteOptions are loaded
    if (siteFromURL && siteOptions?.length > 0) {
      const isValidSite = siteOptions?.find(
        (site: any) => site.uid === siteFromURL
      );

      if (isValidSite) {
        store.dispatch(setSelectedSharedUid(siteFromURL)); // This is likely a page refresh, so restore the selected site
        store.dispatch(setSelectedSite(siteFromURL));
        store.dispatch(setSelectedYear(selectedYear));
        updatedParams.set("selectedYear", selectedYear);
        setSearch(true);
        setDataShow(true);
      } else {
        updatedParams.delete("selectedSite"); // Invalid site in URL, remove it
      }
    }
    if (yearFromURL) {
      dispatch(setSelectedYear(yearFromURL));
    }
    if (statusFromURL) {
      dispatch(setSelectedForecastStatus(statusFromURL));
    }
    setSearchParams(updatedParams, { replace: true });
    // eslint-disable-next-line
  }, [siteOptions]);

  // below portion is for setting and deleting the site
  const updateURLParams = (siteUid: any, year?: string, status?: string) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get existing params
    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the value
    } else {
      updatedParams.delete("selectedSite"); // Remove key if null
    }
    if (year) {
      updatedParams.set("selectedYear", year);
    } else {
      updatedParams.delete("selectedYear");
    }
    if (status) {
      updatedParams.set("selectedStatus", status);
    } else {
      updatedParams.delete("selectedStatus");
    }
    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading
  };

  const handleSearch = () => {
    if (selectedSharedUid) {
      setSearch(true);
      setDataShow(true);
      setTableRefresh(true); // Trigger a refresh to force API call
      store.dispatch(setSelectedSite(selectedSharedUid));
      store.dispatch(setSelectedYear(selectedYear));
      const updatedParams = new URLSearchParams(window.location.search);
      updatedParams.set("selectedSite", selectedSharedUid);
      updatedParams.set("selectedYear", selectedYear);
      setSearchParams(updatedParams, { replace: true });
    }
  };

  const handleSelectWebsite = (_: unknown, newValue: any) => {
    const params = new URLSearchParams(location.search);
    if (!newValue) {
      store.dispatch(setSelectedSharedUid(null));
      store.dispatch(setSelectedSite(null));
      setDataShow(false);
      updateURLParams(null); // Remove selectedSite from URL
    } else {
      store.dispatch(setSelectedSharedUid(newValue.uid));
      store.dispatch(setSelectedSite(newValue.uid));
      setSearch(false);
      setDataShow(false);
      dispatch(setSelectedSharedUid(newValue.uid));
      params.delete("search");
      setSearchParams(params);
    }
  };

  useEffect(() => {
    setDataLoaded(false);
    setTableRefresh(false);
    if (search === true && dataShow && selectedSharedUid?.length !== 0) {
      setLoading(true);
      const fetchForecastData = async () => {
        try {
          await forecastersEndPointLogic.getForecastersList(
            selectedSharedUid,
            oauthTokenHeader,
            selectedYear,
            dispatch
          );
        } catch (error: any) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message:
                error?.parsedText ||
                "Error while fetching data. Please try again later.",
              type: "error",
            })
          );
        } finally {
          setLoading(false);
          setDataLoaded(true);
        }
      };
      fetchForecastData();
    }
    // eslint-disable-next-line
  }, [dataShow, selectedSharedUid]);

  const handleYearChange = (_event: any, newValue: any) => {
    if (newValue) {
      dispatch(setSelectedYear(newValue?.label));
      setSearch(false);
      setDataShow(false);
      updateURLParams(
        selectedSharedUid,
        newValue?.label,
        selectedForecastStatus
      );
    } else {
      dispatch(setSelectedYear(""));
      setSearch(false);
      setDataShow(false);
      updateURLParams(selectedSharedUid, undefined, selectedForecastStatus);
    }
  };

  const handleStatusChange = (_event: any, newValue: any) => {
    if (newValue) {
      dispatch(setSelectedForecastStatus(newValue?.label));
      updateURLParams(selectedSharedUid, selectedYear, newValue?.label);
    } else {
      dispatch(setSelectedForecastStatus(""));
      updateURLParams(selectedSharedUid, selectedYear);
    }
  };

  const hasViewForcastersHistoryPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "view_forecasters_history"
  );

  return (
    <>
      {hasViewForcastersHistoryPermission ? (
        <>
          <Paper sx={{ padding: "8px 16px", mb: 2 }}>
            <Typography fontSize={24}>Forecaster</Typography>
            <Typography
              fontSize={14}
              sx={{ paddingBottom: "15px", color: "rgba(0, 0, 0, 0.6)" }}
            >
              Predict and plan key metrics on a monthly basis, allowing for
              actionable insights and comparisons between forecasted and actual
              data in order to improve future planning accuracy.
            </Typography>
            <Grid container mt={0} columnSpacing={3} mb={1}>
              <Grid item xs={6}>
                <Autocomplete
                  fullWidth
                  disablePortal
                  options={siteOptions}
                  getOptionLabel={(option: any) => option?.siteName}
                  value={
                    siteOptions.find(
                      (option: any) => option?.uid === selectedSharedUid
                    ) || null
                  }
                  onChange={handleSelectWebsite}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Asset"
                      size="small"
                      color="secondary"
                      placeholder="Search site name or choose from the list"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Autocomplete
                  fullWidth
                  disablePortal
                  options={yearList}
                  value={
                    yearList.find(
                      (option: any) => option?.label === selectedYear
                    ) || null
                  }
                  onChange={handleYearChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Year"
                      size="small"
                      color="secondary"
                      placeholder="Select from list"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Autocomplete
                  fullWidth
                  disablePortal
                  options={StatusList}
                  getOptionLabel={(option: any) =>
                    option.label.charAt(0).toUpperCase() + option.label.slice(1)
                  }
                  value={
                    StatusList.find(
                      (option: any) => option?.label === selectedForecastStatus
                    ) || null
                  }
                  onChange={handleStatusChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      size="small"
                      color="secondary"
                      placeholder="Select from list"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!selectedSharedUid || !selectedYear}
                  onClick={handleSearch}
                  data-tracker="125"
                  size="large"
                  color="secondary"
                  sx={{ py: "7px", px: "10px", whiteSpace: "nowrap" }}
                >
                  View Forecast
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {dataShow && (
            <>
              {loading ? (
                <SimpleBackdrop />
              ) : dataLoaded ? (
                <>
                  <ForeCasterTableCard />
                  <ForeCastDiscussion />
                  {selectedSharedUid ? (
                    <div data-tracker="448">
                      <FloatingActionItems
                        selectedSiteFloatingAction={selectedSharedUid}
                        selectedDimensionsFloatingAction={""}
                        trackIdFloatingAction={"447"}
                      />
                    </div>
                  ) : (
                    ""
                  )}
                </>
              ) : null}
            </>
          )}
        </>
      ) : (
        <Page404 />
      )}
    </>
  );
};

export default PerformanceForCaster;
